/** Apparel BOM model. */
import { C8Node, ICache } from "csi/typings";
import WorkingCache from "csi/state/WorkingCache";
import xml from "csi/xml";
import reflection from "csi/reflection";
import crews from "csi/access/crews";

const ADMIN_PRODUCT_SPEC = "Product Specification Administrator";
const ADMIN_SITE = "SiteAdministrator";
const ADMIN_TDS_BOM = "TDS - BOM Administrator";
const EDITOR_PRODUCT_SPEC = "Product Specification Editor";
const EDITOR_TDS_BOM = "TDS - BOM Editor";
const BOM_EDITOR_ROLES = [ADMIN_PRODUCT_SPEC, ADMIN_SITE, ADMIN_TDS_BOM, EDITOR_PRODUCT_SPEC, EDITOR_TDS_BOM];
const NEW_STYLE_SOURCE_BOM_ROLES = [ADMIN_PRODUCT_SPEC, ADMIN_SITE, ADMIN_TDS_BOM, EDITOR_PRODUCT_SPEC,
	EDITOR_TDS_BOM, "TDS - BOM Viewer"];

export const BOM_COMPARE_REVISION = "BomCompareRevision";
export const STYLE_BOM_COMPARE = "StyleBomCompare";
export const THEME_BOM_COMPARE = "ThemeBomCompare";
export const COMPARE_WITH_SOURCE_BOM = "BomCompareWithSourceBom";
export const COMPARE_WITH_HOST_BOM = "BomCompareWithHostBom";
export const BOM_THEME_PARENT_PATH = "Child:Theme/Child:__Parent__";

export default class ApparelBom {
	/** cache */
	readonly cache: ICache;

	/** current node page */
	currentNode: C8Node;

	/** Verify if the current node is a HostBom, which means it will have associated source bom. */
	static isHostBOM(currentNode: C8Node): boolean {
		return xml.isEmptyURL(currentNode?.LinkedHostDataSheet)
			&& xml.isContainerValueValid(currentNode?.LinkedSourceDataSheets, xml.ATTR_SET);
	}

	/** Verify if current node is a SourceBOM, which means was created based on HostBom.     */
	static isSourceBOM(currentNode: C8Node): boolean {
		return !!currentNode && xml.isValidURL(currentNode?.LinkedHostDataSheet);
	}

	/** Verify if is Style BOM and has any node with ThemeBomFeature as StyleBOM. **/
	static hasStyleThemeBOM(currentNode: C8Node, cache: ICache): boolean {
		const { isSourceBOM } = ApparelBom;

		if (!currentNode || isSourceBOM(currentNode)) {
			return false;
		}
		const parentNode = cache.getPathNode(currentNode, "Child:__Parent__");
		if (parentNode?.$Type === "Style") {
			const themeMaster = cache.getPathNode(parentNode, BOM_THEME_PARENT_PATH);
			return !!themeMaster && ApparelBom._hasThemeBomFeature(themeMaster);
		}

		return false;
	}

	/**
	 * Checks ApparelBom type if it is: HostBom, SourceBom and StyleHas any Theme Bom.
	 * @return the allowed menu items to be compared with ApparelBom.
	 */
	static bomCompareMenuItems(currentNode: C8Node, cache: ICache): string[] {
		const response = [];

		const _isHostBOM = ApparelBom.isHostBOM(currentNode);
		const _isSourceBOM = ApparelBom.isSourceBOM(currentNode);
		if (_isHostBOM) {
			response.push(STYLE_BOM_COMPARE, COMPARE_WITH_SOURCE_BOM);
		} else if (_isSourceBOM && ApparelBom.userHasAccessToProductAndAssignedRoles(currentNode, cache)) {
			response.push(COMPARE_WITH_HOST_BOM);
		} else if (!_isHostBOM && !_isSourceBOM) {
			response.push(STYLE_BOM_COMPARE);
		}

		if (ApparelBom.hasStyleThemeBOM(currentNode, cache)) {
			response.push(THEME_BOM_COMPARE);
		}

		return response;
	}

	/** Checks if the user has access to the product when provided an ApparelBOM node */
	static userHasAccessToProductAndAssignedRoles(node: C8Node, cache: ICache): boolean {
		const hasAccessToProduct = !!cache.getPathNode(node.$URL, "Child:__Parent__/Child:__Parent__/Child:__Parent__");
		const hasAccess = crews.isInGlobalRoles(NEW_STYLE_SOURCE_BOM_ROLES) || crews.isInRoles(NEW_STYLE_SOURCE_BOM_ROLES, node);
		return hasAccessToProduct && hasAccess;
	}

	/** Checks if current user has at least one of the BOM editor roles {@link BOM_EDITOR_ROLES}. */
	static userHasBomEditorRole(currentNode: C8Node): boolean {
		return crews.isInGlobalRoles(BOM_EDITOR_ROLES) || crews.isInRoles(BOM_EDITOR_ROLES, currentNode);
	}

	static _hasThemeBomFeature(themeMaster: C8Node): boolean {
		return reflection.getSubtypeSetting(themeMaster?.Subtype, "ThemeBOMFeature") === "ThemeBOMFeature:StyleBOM";
	}

	/** class constructor */
	constructor(node: C8Node, cache: ICache = new WorkingCache()) {
		this.currentNode = node;
		this.cache = cache;
		cache.add(node);
	}
}